import React, { useState, useEffect } from 'react';
import {
  faTrophyStar,
  faRankingStar,
  faBellRing,
} from '@fortawesome/pro-solid-svg-icons';
import NavBarHome from '../Layouts/NavBarHome';
import FooterHome from '../Layouts/FooterHome';
import PageHeader from '../pagecomponents/PageHeader';
import profilingScreenshot from '../pagecomponents/screenshots/manager/5. PlanFollowUp.png';
import placeholderComponent from '../pagecomponents/screenshots/placeholdercomponent.png';

// Nuevas imágenes para las secciones
import achivementsImg from '../pagecomponents/screenshots/follow/achivements.png';
import leaderPanelImg from '../pagecomponents/screenshots/follow/leaderpanel.png';
import alertsImg from '../pagecomponents/screenshots/follow/alerts.png';

import SectionBodyLeft from '../pagecomponents/SectionBodyLeft';
import SectionBodyRight from '../pagecomponents/SectionBodyRight';
import EconomicImpactStats from '../pagecomponents/EconomicImpactStats';
import FAQ from '../pagecomponents/FAQ';

const styles = {
  container: {
    fontFamily: "'Helvetica Neue', Arial, sans-serif",
    fontWeight: '400',
    margin: 0,
    padding: 0,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  },
  mainSection: {
    flex: 1,
    backgroundColor: '#f9f9f9',
  },
  sectionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '20px',
    padding: '40px 20px',
    backgroundColor: '#fff',
  },
};

function FollowPage() {
  const [language, setLanguage] = useState(
    () => localStorage.getItem('language') || 'es'
  );

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const content = {
    es: {
      hero: {
        title: 'Sigue. Ajusta. Acompaña el progreso de tu empleado.',
        intro:
          'Apoya el crecimiento de tu equipo con visibilidad total. Nuestra herramienta de seguimiento te permite detectar quién avanza, quién se ha detenido y quién necesita tu intervención.',
        cta: 'Reservar Demo',
      },
      sections: [
        {
          title: 'Seguimiento de hábitos con dinámicas de competición',
          texts: [
            'Demuestra tus conocimientos y dedica esfuerzo a cada microacción para ganar medallas que reconocen tu aprendizaje.',
            'Medallas de persistencia: alcanza hitos consecutivos para desbloquear medallas exclusivas de constancia.',
            'Rankings internos: consulta en tiempo real los tableros de competición. Sigue de cerca la clasificación y reta a tu equipo a alcanzar nuevas metas juntos.',
          ],
        },
        {
          title: 'Panel del Líder',
          texts: [
            'Alertas de estado del equipo (activo, en pausa, bloqueado).',
            'Sigue de cerca al equipo: consulta en tiempo real los tableros de persistencia de los planes.',
            'Notificaciones de tendencias predictivas para anticipar lo que puede venir.',
          ],
        },
        {
          title: 'Alertas y notificaciones inteligentes',
          texts: [
            'Alertas tempranas: cuando algo empieza a no ir bien, no cuando ya es tarde.',
            'Recordatorios proactivos durante el avance de los planes.',
            'Sugerencias de intervención: avisos que te proponen lo que podrías empezar con cada talento.',
          ],
        },
      ],
    },
    en: {
      hero: {
        title: 'Follow. Adjust. Support your employee’s progress.',
        intro:
          'Empower your team’s growth with full visibility. Our tracking tool lets you see who’s moving forward, who’s stalled, and who needs your intervention.',
        cta: 'Book Demo',
      },
      sections: [
        {
          title: 'Habit Tracking with Competitive Dynamics',
          texts: [
            'Demonstrate your knowledge and effort in every micro-action to earn badges that recognize your learning.',
            'Persistence Badges: hit consecutive milestones to unlock exclusive badges for consistency.',
            'Internal Rankings: view live competition boards, track standings in real time, and challenge your team to reach new goals together.',
          ],
        },
        {
          title: 'Leader Dashboard',
          texts: [
            'Team status alerts (active, paused, blocked).',
            'Monitor your team closely: view persistence boards against their plans in real time.',
            'Predictive trend notifications to anticipate what’s coming next.',
          ],
        },
        {
          title: 'Smart Alerts and Notifications',
          texts: [
            'Early warnings: get alerted when something starts to go off track, not after it’s too late.',
            'Proactive reminders throughout plan progress.',
            'Intervention Suggestions: prompts offering actions you can take with each team member.',
          ],
        },
      ],
    },
  };

  const lang = content[language] || content.es;
  const handleDemoClick = () => console.log('Reservar Demo clicked');

  const icons = [faTrophyStar, faRankingStar, faBellRing];

  // Array de imágenes por sección
  const sectionImages = [achivementsImg, leaderPanelImg, alertsImg];

  // Construcción de las secciones con sus imágenes
  const sections = lang.sections.map((item, idx) => ({
    icon: icons[idx],
    title: item.title,
    imageSrc: sectionImages[idx] || placeholderComponent,
    texts: item.texts,
  }));

  const impactStats = [
    {
      number: '3.2/5',
      desc:
        language === 'es'
          ? 'Calificación media de desempeño de los empleados'
          : 'Average employee performance rating',
    },
    {
      number: '19%',
      desc:
        language === 'es'
          ? 'Empleados que se marchan son críticos para el negocio'
          : 'Employees who churn are critical to the business',
    },
    {
      number: '17%',
      desc:
        language === 'es'
          ? 'Rotación media anual de empleados'
          : 'Average annual employee turnover',
    },
  ];

  const faqs = {
    es: [
      {
        question: '¿Cómo detecta la plataforma el progreso de mis empleados?',
        answer:
          'Registra cada microacción completada, el feedback del manager y del empleado en tiempo real para indicar tendencias de quién avanza o quién necesita apoyo.',
      },
      {
        question:
          '¿Qué dinámicas de gamificación incluye el seguimiento de hábitos?',
        answer:
          'Incorpora medallas, premios de persistencia y rankings internos para fomentar la competencia sana.',
      },
      {
        question: '¿Qué funcionalidades ofrece el Panel del Líder?',
        answer:
          'Un dashboard unificado con filtros inteligentes (por equipo, competencia o fase) y tendencias que muestran estados activos, en pausa o bloqueados.',
      },
      {
        question: '¿Cómo funcionan las alertas y notificaciones inteligentes?',
        answer:
          'Envía alertas tempranas cuando hay tareas pendientes, recordatorios proactivos para reactivar el avance y sugerencias de mentoring.',
      },
    ],
    en: [
      {
        question: 'How does the platform detect my employees’ progress?',
        answer:
          'It records each completed micro-action, as well as manager and employee feedback in real time, to highlight who is advancing and who needs support.',
      },
      {
        question: 'What gamification dynamics are included in habit tracking?',
        answer:
          'It incorporates badges, persistence rewards, and internal rankings to foster healthy competition.',
      },
      {
        question: 'What features does the Leader Dashboard offer?',
        answer:
          'A unified dashboard with smart filters (by team, skill or phase) and trends showing active, paused, or blocked statuses.',
      },
      {
        question: 'How do smart alerts and notifications work?',
        answer:
          'They send early warnings when tasks are pending, proactive reminders to restart progress, and mentoring suggestions.',
      },
    ],
  };

  return (
    <div style={styles.container}>
      <NavBarHome language={language} />

      <PageHeader
        title={lang.hero.title}
        subtitle={lang.hero.intro}
        ctaText={lang.hero.cta}
        onCtaClick={handleDemoClick}
        imageSrc={profilingScreenshot}
      />

      <main style={styles.mainSection}>
        <div style={styles.sectionsContainer}>
          {sections.map((sec, i) => {
            // Index 0 y 2: izquierda; index 1: derecha
            const Component = i % 2 === 0 ? SectionBodyLeft : SectionBodyRight;
            return (
              <Component
                key={i}
                icon={sec.icon}
                title={sec.title}
                imageSrc={sec.imageSrc}
                texts={sec.texts}
              />
            );
          })}
        </div>

        {/* Economic Impact Stats Section */}
        <EconomicImpactStats stats={impactStats} language={language} />

        {/* FAQs Section */}
        <FAQ items={faqs[language]} language={language} />
      </main>

      <FooterHome language={language} setLanguage={setLanguage} />
    </div>
  );
}

export default FollowPage;
